import {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import type {
  RelativeCallView,
  RelativeJoinGrant,
  RelativeResolveResponse,
} from "@shared/types/callRelative";
import {
  answerCall,
  CallApiError,
  consumeJoinLink,
  declineCall,
  endCall,
  fetchCurrent,
  fetchSession,
  inviteRoom,
  resolveToken,
} from "./api";
import { makeT, svPossessive } from "@shared/relative-strings";
import { NotifyPrompt } from "./NotifyPrompt";
import { refreshSubscription } from "./push";
import type { RelativeCurrentResponse } from "@shared/types/callRelative";

const CallScreen = lazy(() => import("./CallScreen"));

type Phase = "loading" | "landing" | "ringing" | "incall" | "ended" | "error";

const LOCALE_KEY = "relative.locale";
const POLL_MS = 3000;

/** Best-effort locale before the session resolves (loading + error screens). */
function initialLocale(): string {
  try {
    return localStorage.getItem(LOCALE_KEY) || "sv";
  } catch {
    return "sv";
  }
}

/** Nordic locales take a bare genitive ("Karins"); English keeps "'s"; others
 *  use the raw name in the templated form. */
function possessiveName(name: string, locale: string): string {
  return ["sv", "no", "da"].includes(locale) ? svPossessive(name) : name;
}

export default function App() {
  const [phase, setPhase] = useState<Phase>("loading");
  const [session, setSession] = useState<RelativeResolveResponse | null>(null);
  const [locale, setLocale] = useState(initialLocale);
  const [errorCode, setErrorCode] = useState<string>("network");
  const [ringing, setRinging] = useState<RelativeCallView | null>(null);
  const [grant, setGrant] = useState<RelativeJoinGrant | null>(null);
  // VC-26 outbound: the server's honest reason a ring was blocked, shown as-is.
  const [callBlock, setCallBlock] = useState<string | null>(null);
  const [calling, setCalling] = useState(false);
  const bootstrapped = useRef(false);
  // A ref mirror of the phase so the polling loop reads the live value without
  // being torn down and rebuilt every transition.
  const phaseRef = useRef<Phase>("loading");
  phaseRef.current = phase;

  const t = makeT(locale);

  const accept = useCallback((s: RelativeResolveResponse) => {
    setSession(s);
    setLocale(() => {
      // Honour a saved choice; otherwise open in the premise default.
      try {
        const saved = localStorage.getItem(LOCALE_KEY);
        if (saved && s.supportedLocales.includes(saved)) return saved;
      } catch {
        /* ignore */
      }
      return s.defaultLocale || "sv";
    });
    setPhase("landing");
    // Heal any silently-rotated push endpoint on every open (no prompt, no UI).
    void refreshSubscription();
  }, []);

  // Apply whatever a fresh session says is live - used by the join deep link so
  // a push lands straight on the ring, not the landing screen.
  const applyCurrent = useCallback((current: RelativeCurrentResponse) => {
    const { call, grant: activeGrant } = current;
    if (call && call.state === "ringing") {
      setRinging(call);
      setPhase("ringing");
    } else if (call && call.state === "active" && activeGrant) {
      setGrant(activeGrant);
      setRinging(call);
      setPhase("incall");
    }
  }, []);

  // ── Bootstrap: trade the token, or re-read an existing session ────────────
  useEffect(() => {
    if (bootstrapped.current) return; // guard StrictMode double-invoke
    bootstrapped.current = true;

    const fail = (code: string) => {
      setErrorCode(code);
      setPhase("error");
    };

    // A push notification's deep link: a single-use join link that trades for
    // the cookie AND tells us what is ringing, so we open on the ring screen.
    const joinMatch = window.location.pathname.match(/^\/j\/(.+)$/);
    if (joinMatch) {
      const token = decodeURIComponent(joinMatch[1]);
      consumeJoinLink(token)
        .then(({ session, current }) => {
          window.history.replaceState({}, "", "/");
          accept(session);
          applyCurrent(current);
        })
        .catch((err: unknown) =>
          fail(err instanceof CallApiError ? err.code : "network")
        );
      return;
    }

    const match = window.location.pathname.match(/^\/c\/(.+)$/);
    if (match) {
      const token = decodeURIComponent(match[1]);
      resolveToken(token)
        .then((s) => {
          // The token is a one-time bearer now exchanged for the cookie - strip
          // it from the URL/history so it never lands in a screenshot or a
          // shared link, exactly as the guest companion does.
          window.history.replaceState({}, "", "/");
          accept(s);
        })
        .catch((err: unknown) =>
          fail(err instanceof CallApiError ? err.code : "network")
        );
      return;
    }

    fetchSession()
      .then(accept)
      .catch((err: unknown) =>
        fail(err instanceof CallApiError ? err.code : "unauthorized")
      );
  }, [accept, applyCurrent]);

  // ── Foreground ring poll ──────────────────────────────────────────────────
  // While we are on the landing or ringing screen, ask the server every few
  // seconds whether the room is calling. (Background waking - a ring when the
  // app is closed - is Web Push, VC-23.)
  useEffect(() => {
    if (!session || !session.canReceiveFromRoom) return;
    if (phase !== "landing" && phase !== "ringing") return;

    let cancelled = false;
    const poll = async () => {
      try {
        const { call, grant: activeGrant } = await fetchCurrent();
        if (cancelled) return;
        const p = phaseRef.current;
        if (p !== "landing" && p !== "ringing") return;

        if (call && call.state === "ringing") {
          setRinging(call);
          setPhase("ringing");
        } else if (call && call.state === "active" && activeGrant) {
          // Already answered (another tab, or a reload mid-call) - rejoin.
          setGrant(activeGrant);
          setRinging(call);
          setPhase("incall");
        } else {
          // Nothing live - a ring that expired or was declined returns us home.
          setRinging(null);
          if (p === "ringing") setPhase("landing");
        }
      } catch {
        /* transient - try again next tick */
      }
    };

    void poll();
    const id = window.setInterval(poll, POLL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [session, phase]);

  const onLocale = (loc: string) => {
    setLocale(loc);
    try {
      localStorage.setItem(LOCALE_KEY, loc);
    } catch {
      /* ignore */
    }
  };

  const onAnswer = async () => {
    if (!ringing) return;
    try {
      const g = await answerCall(ringing.callId);
      setGrant(g);
      setPhase("incall");
    } catch {
      // Someone else answered/it expired between the ring and the tap.
      setRinging(null);
      setPhase("landing");
    }
  };

  const onDecline = async () => {
    if (!ringing) return;
    const id = ringing.callId;
    setRinging(null);
    setPhase("landing");
    try {
      await declineCall(id);
    } catch {
      /* best-effort */
    }
  };

  // ── VC-26: the relative rings the room ────────────────────────────────────
  const onCallRoom = async () => {
    if (calling) return;
    setCalling(true);
    setCallBlock(null);
    try {
      const res = await inviteRoom();
      if (res.ok) {
        // The room is ringing - join the media room and wait, exactly as the
        // answer path does. CallScreen shows the "calling…" state until the
        // resident picks up on the TV.
        setGrant(res.grant);
        setRinging(res.call);
        setPhase("incall");
      } else {
        // A blocked call is never silent: show the server's kind, specific
        // reason verbatim ("… nattvila 21:00-08:00").
        setCallBlock(res.message);
      }
    } catch (err) {
      if (err instanceof CallApiError && err.status === 401) {
        setErrorCode("unauthorized");
        setPhase("error");
      } else {
        setCallBlock(t("call_room_failed"));
      }
    } finally {
      setCalling(false);
    }
  };

  const leaveCall = useCallback(() => {
    const id = grant?.callId ?? ringing?.callId;
    setGrant(null);
    setRinging(null);
    setPhase("ended");
    if (id) void endCall(id).catch(() => {});
  }, [grant, ringing]);

  // ── Render ────────────────────────────────────────────────────────────────
  if (phase === "loading") {
    return (
      <div className="screen screen--center">
        <div
          className="spinner"
          aria-label={makeT(initialLocale())("loading")}
        />
      </div>
    );
  }

  if (phase === "error") {
    const network = errorCode === "network";
    return (
      <div className="screen screen--center">
        <div className="card">
          <h1 className="card__title">
            {network ? t("err_network_title") : t("err_invalid_title")}
          </h1>
          <p className="card__body">
            {network ? t("err_network_body") : t("err_invalid_body")}
          </p>
          {network && (
            <button
              type="button"
              className="btn btn--primary"
              onClick={() => window.location.reload()}
            >
              {t("retry")}
            </button>
          )}
        </div>
      </div>
    );
  }

  if (phase === "incall" && grant) {
    return (
      <Suspense
        fallback={
          <div className="screen screen--center screen--dark">
            <div className="spinner spinner--light" />
          </div>
        }
      >
        <CallScreen
          grant={grant}
          peerName={session?.residentName || ""}
          t={t}
          onEnded={leaveCall}
          onHangUp={leaveCall}
        />
      </Suspense>
    );
  }

  if (phase === "ended") {
    return (
      <div className="screen screen--center">
        <div className="card">
          <h1 className="card__title">{t("call_ended")}</h1>
          <p className="card__body">{t("call_ended_hint")}</p>
          <button
            type="button"
            className="btn btn--ghost"
            onClick={() => setPhase("landing")}
          >
            {t("back")}
          </button>
        </div>
      </div>
    );
  }

  const name = session?.residentName ?? "";
  const headline = name
    ? t("connected_to", { name: possessiveName(name, locale) })
    : t("connected_to_generic");

  // ── Ringing overlay (full-screen) ─────────────────────────────────────────
  if (phase === "ringing" && ringing) {
    return (
      <div className="screen screen--dark ring">
        <div className="ring__pulse" aria-hidden="true" />
        <p className="ring__label">{t("incoming")}</p>
        <h1 className="ring__name">{name || t("connected_to_generic")}</h1>
        <div className="ring__actions">
          <button
            type="button"
            className="ring__btn ring__btn--decline"
            onClick={onDecline}
          >
            <span className="ring__icon" aria-hidden="true">
              📵
            </span>
            {t("decline")}
          </button>
          <button
            type="button"
            className="ring__btn ring__btn--answer"
            onClick={onAnswer}
          >
            <span className="ring__icon" aria-hidden="true">
              📞
            </span>
            {t("answer")}
          </button>
        </div>
      </div>
    );
  }

  // ── Landing ────────────────────────────────────────────────────────────────
  return (
    <div className="screen landing">
      <header className="landing__head">
        {session?.premiseName && (
          <p className="landing__premise">{session.premiseName}</p>
        )}
      </header>

      <main className="landing__main">
        <div className="landing__badge" aria-hidden="true">
          <span className="landing__badge-dot" />
        </div>
        <h1 className="landing__headline">{headline}</h1>
        <p className="landing__hint">
          {session?.canReceiveFromRoom ? t("waiting_hint") : ""}
        </p>

        {session?.canCallRoom && (
          <div className="landing__call">
            <button
              type="button"
              className="btn btn--primary btn--call"
              onClick={onCallRoom}
              disabled={calling}
              aria-busy={calling}
            >
              {calling ? t("call_room_calling") : t("call_room")}
            </button>
            {callBlock && (
              <p className="landing__note" role="status">
                {callBlock}
              </p>
            )}
          </div>
        )}

        {/* Ring delivery upsell - only where the room can actually ring this
            phone. After the first successful visit, ask to enable notifications
            (Android) or add to home screen (iOS). Never blocks the call UI. */}
        {session?.canReceiveFromRoom && (
          <NotifyPrompt t={t} residentName={name} locale={locale} />
        )}
      </main>

      <footer className="landing__foot">
        {session && session.supportedLocales.length > 1 && (
          <div className="langs" role="group" aria-label="Language">
            {session.supportedLocales.map((loc) => (
              <button
                key={loc}
                type="button"
                className="lang"
                data-active={loc === locale ? "true" : "false"}
                onClick={() => onLocale(loc)}
              >
                {loc.toUpperCase()}
              </button>
            ))}
          </div>
        )}
        <PrivacyNote t={t} />
      </footer>
    </div>
  );
}

function PrivacyNote({ t }: { t: ReturnType<typeof makeT> }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="privacy">
      <button
        type="button"
        className="privacy__toggle"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
      >
        {t("privacy")}
      </button>
      {open && <p className="privacy__text">{t("privacy_text")}</p>}
    </div>
  );
}
